"use client";

import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import {
  useRef,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { cn } from "@/lib/utils";
import { useCursor } from "@/providers/cursor-provider";

type ButtonVariant = "primary" | "secondary" | "ghost" | "outline";
type ButtonSize = "sm" | "md" | "lg";

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-foreground text-background hover:bg-accent hover:text-white shadow-[var(--shadow-soft)]",
  secondary:
    "bg-gradient-to-r from-accent via-accent-secondary to-accent-tertiary bg-[length:170%_100%] text-white shadow-[var(--shadow-glow)] hover:brightness-110 hover:bg-[length:100%_100%]",
  ghost: "bg-transparent text-foreground hover:bg-foreground/5",
  outline:
    "border border-border-strong bg-transparent text-foreground hover:border-accent hover:text-accent hover:bg-accent/5",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-10 px-4 text-sm",
  md: "h-12 px-5 text-sm",
  lg: "h-14 px-7 text-base",
};

type CommonProps = {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  arrow?: boolean;
  magnetic?: boolean;
  cursorLabel?: string;
};

type ButtonAsButton = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined;
  };

type ButtonAsLink = CommonProps & {
  href: string;
};

export function Button(props: ButtonAsButton | ButtonAsLink) {
  const {
    children,
    variant = "primary",
    size = "md",
    className,
    arrow = false,
    magnetic = true,
    cursorLabel = "OPEN",
  } = props;

  const ref = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 220, damping: 18 });
  const springY = useSpring(y, { stiffness: 220, damping: 18 });
  const { setLabel } = useCursor();

  const onMove = (e: MouseEvent) => {
    if (!magnetic || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width / 2);
    const dy = e.clientY - (rect.top + rect.height / 2);
    x.set(dx * 0.18);
    y.set(dy * 0.18);
  };

  const onLeave = () => {
    x.set(0);
    y.set(0);
    setLabel(null);
  };

  const classes = cn(
    "magnetic-btn relative inline-flex items-center justify-center gap-2 rounded-full font-medium tracking-tight transition-colors duration-300",
    variants[variant],
    sizes[size],
    className,
  );

  const content = (
    <>
      <span>{children}</span>
      {arrow ? <ArrowUpRight className="h-4 w-4" aria-hidden /> : null}
    </>
  );

  if ("href" in props && props.href) {
    return (
      <motion.div style={{ x: springX, y: springY }} className="inline-flex">
        <Link
          ref={ref as RefObject<HTMLAnchorElement>}
          href={props.href}
          className={classes}
          onMouseMove={onMove}
          onMouseEnter={() => setLabel(cursorLabel)}
          onMouseLeave={onLeave}
        >
          {content}
        </Link>
      </motion.div>
    );
  }

  const buttonProps = props as ButtonAsButton;
  return (
    <motion.div style={{ x: springX, y: springY }} className="inline-flex">
      <button
        ref={ref as RefObject<HTMLButtonElement>}
        type={buttonProps.type ?? "button"}
        className={classes}
        onMouseMove={onMove}
        onMouseEnter={() => setLabel(cursorLabel)}
        onMouseLeave={onLeave}
        onClick={buttonProps.onClick}
        disabled={buttonProps.disabled}
      >
        {content}
      </button>
    </motion.div>
  );
}